import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import useValidation from "../../../../hooks/useValidation";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import { travelCancellationReasons } from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/data";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";

const TravelCancellationReasonPage = ({
  handleNavigationToPolicyOverview,
  setSelectedReasonOut,
}) => {
  const { handleNextSubstep, handlePrevStep } = usePolicyCancellationFlow();
  const translate = useLanguageTranslate();

  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const { errors, validateFields } = useValidation();

  // Debug: Log when component mounts/unmounts
  useEffect(() => {
    console.log("🟢 TravelCancellationReasonPage MOUNTED");
    return () => {
      console.log("🔴 TravelCancellationReasonPage UNMOUNTED");
    };
  }, []);

  // Debug: Log state changes
  useEffect(() => {
    console.log("📊 State changed - selectedReason:", selectedReason);
  }, [selectedReason]);

  const validationConfig = [
    { name: "selectedReason", validations: ["required"] },
    {
      name: "otherReason",
      validations: selectedReason == "Others" ? ["required"] : [],
    },
  ];

  const handleRadioChange = (reasonLabel, translatedReason) => {
    console.log("🔵 Radio change clicked:", reasonLabel, translatedReason);
    console.log("📍 Before setState - selectedReason:", selectedReason);

    setSelectedReason(reasonLabel); // Store the English label for comparison
    console.log(
      "📍 After setSelectedReason called (state may not update immediately)"
    );

    setSelectedReasonOut(translatedReason); // Store the translated value to send to API
    console.log("📍 After setSelectedReasonOut called");

    if (reasonLabel !== "Others") {
      setOtherReason("");
    }

    console.log("📍 handleRadioChange completed");
  };

  const { t } = useTranslation();

  const continueClickHandler = (e) => {
    console.log("Reason for cancellation: ", selectedReason, otherReason);

    e.preventDefault();

    const isFormValid = validateFields(validationConfig, {
      selectedReason,
      otherReason,
    });
    if (!isFormValid) {
      return;
    }
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      btn_onClick={continueClickHandler}
      backClickHandler={handlePrevStep}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          btn_onClick={continueClickHandler}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg max767:p-5 max767:mb-8 max767:pt-0 pb-20 max767:pb-[50px]">
        <div className="flex justify-center items-center w-full">
          <div className="p-0 w-full">
            <TitleDescription
              title={translate("Reason for Cancellation", "سبب الإلغاء")}
              description={translate(
                "Please review your policy details carefully before proceeding with cancellation.",
                "ساعدنا في فهم سبب رغبتك في إلغاء سياسة التأمين الخاصة بك. إن ملاحظاتك قيّمة وسيتم استخدامها لتحسين خدماتنا."
              )}
            />

            <form className="space-y-4">
              {travelCancellationReasons?.map((reason, index) => {
                const isSelected = selectedReason === reason?.label;
                const translatedLabel = translate(
                  reason?.label,
                  reason?.label_ar
                );

                return (
                  <div
                    key={index}
                    onClick={() =>
                      handleRadioChange(reason?.label, translatedLabel)
                    }
                    className={`flex justify-start items-center gap-4 self-stretch border px-4 py-3 rounded-[10px] border-solid cursor-pointer transition-all hover:border-[#00719A] hover:bg-[#E3F8FF] ${
                      isSelected
                        ? "border-[#00719A] bg-[#E3F8FF]"
                        : "border-[#DDE2E4] bg-[#FFFFFF]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancellationReason"
                      id={`reason-${index}`}
                      value={reason?.label}
                      checked={isSelected}
                      onChange={() =>
                        handleRadioChange(reason?.label, translatedLabel)
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="size-5 text-blue-600 border-gray-300 cursor-pointer !outline-none"
                    />
                    <div className="flex flex-col w-full">
                      <label
                        htmlFor={`reason-${index}`}
                        className="text-[#1A2024] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] mb-0.5 cursor-pointer"
                      >
                        {translatedLabel}
                      </label>

                      {selectedReason == "Others" &&
                      reason.label == "Others" ? (
                        <div
                          className="w-full"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder={translate(
                              "Other reasons (please specify)",
                              "أسباب أخرى (يرجى التحديد)"
                            )}
                            value={otherReason}
                            onChange={(e) => setOtherReason(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-black placeholder:text-lg placeholder:not-italic placeholder:font-normal placeholder:leading-[26px] placeholder:tracking-[-0.18px] placeholder:opacity-50"
                          />
                          {errors.otherReason && (
                            <p className="text-red-500 text-sm">
                              {translate(
                                "Please specify other reason.",
                                "يرجى تحديد سبب آخر"
                              )}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-[#1A2024] text-base not-italic font-normal leading-6 tracking-[-0.16px]">
                          {translate(
                            reason?.description,
                            reason?.description_ar
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              {errors.selectedReason && (
                <p className="text-red-500 text-sm">
                  {translate(
                    "Please select any reason to proceed.",
                    "يرجى اختيار أي سبب للمتابعة."
                  )}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default TravelCancellationReasonPage;
